/**
 * Suggested Prompts Component
 * Displays suggested prompts for users to quickly start conversations
 * Shows 6 random prompts at a time from the selected agent
 */

import { useState, useEffect } from 'react';
import { Sparkles, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAgent } from '@/context/AgentContext';
import { useAdmin } from '@/context/AdminContext';
import { useAuth } from '@/context/AuthContext';
import type { AgentSuggestedPrompt } from '@/types/agent.types';
import { cn } from '@/utils/classNames';

// Fisher-Yates shuffle, then take the first `count` items. Replaces the
// previous `sort(() => Math.random() - 0.5)` which is statistically biased.
function getRandomPrompts(
  prompts: AgentSuggestedPrompt[],
  count: number
): AgentSuggestedPrompt[] {
  const shuffled = prompts.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}

/**
 * Returns a time-of-day greeting ("Good morning" / "Good afternoon" /
 * "Good evening") for the given 24-hour clock hour. Boundaries: morning is
 * before noon, afternoon is noon–16:59, evening is 17:00 onward.
 */
export function getTimeOfDayGreeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

interface SuggestedPromptsProps {
  onSelectPrompt: (prompt: string) => void;
  className?: string;
}

export function SuggestedPrompts({
  onSelectPrompt,
  className = '',
}: SuggestedPromptsProps) {
  const { selectedAgent, isLoading } = useAgent();
  const agentPrompts = selectedAgent?.suggestedPrompts || [];

  // Show 6 random prompts, rotate on component mount or prompt list change
  const [displayedPrompts, setDisplayedPrompts] = useState<
    AgentSuggestedPrompt[]
  >([]);

  useEffect(() => {
    if (agentPrompts.length > 0) {
      setDisplayedPrompts(getRandomPrompts(agentPrompts, 6));
    } else {
      setDisplayedPrompts([]);
    }
    // Only re-shuffle when agent changes or number of prompts changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent?.id, agentPrompts.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div
          className="h-6 w-6 animate-spin rounded-full border-2
          border-stellar-primary border-t-transparent"
        />
      </div>
    );
  }

  if (displayedPrompts.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-stellar-primary" />
        <h3 className="text-sm font-semibold text-text-secondary">
          Suggested Prompts
        </h3>
      </div>

      {/* Prompt Grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {displayedPrompts.map((prompt, index) => (
          <PromptCard
            key={`${prompt.title}-${index}`}
            prompt={prompt}
            onClick={() => onSelectPrompt(prompt.prompt)}
          />
        ))}
      </div>
    </div>
  );
}

interface PromptCardProps {
  prompt: AgentSuggestedPrompt;
  onClick: () => void;
}

function PromptCard({ prompt, onClick }: PromptCardProps) {
  // PromptCard backgrounds use the same neutral surface across categories
  // so they look consistent in both themes; the category accent comes
  // through on the icon + label colour below. Previously each category
  // had its own light-tinted bg/border (bg-orange-50, bg-pink-50, etc.)
  // which read as out-of-place pastel cards on the dark background.
  const categoryColors: Record<string, string> = {
    general:       'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    documents:     'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    data:          'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    collaboration: 'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    writing:       'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    learning:      'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    coding:        'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    creative:      'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    productivity:  'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    analysis:      'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
    custom:        'bg-bg-tertiary border-border-default hover:bg-bg-secondary',
  };

  // Category-tinted text colour for the prompt label. Saturated mid-tone
  // colours work on both light and dark surfaces (no -50/-light shades
  // that wash out, no -800/-900 shades that disappear).
  const categoryTextColors: Record<string, string> = {
    general:       'text-stellar-primary',
    documents:     'text-success',
    data:          'text-purple-500',
    collaboration: 'text-orange-500',
    writing:       'text-indigo-500',
    learning:      'text-warning',
    coding:        'text-cyan-500',
    creative:      'text-pink-500',
    productivity:  'text-teal-500',
    analysis:      'text-violet-500',
    custom:        'text-text-primary',
  };

  const category = prompt.category || 'general';

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative rounded-xl border-2 p-4 text-left',
        'transition-all duration-200',
        'hover:shadow-md hover:scale-105',
        'focus:outline-none focus:ring-2 focus:ring-stellar-primary',
        'focus:ring-offset-2',
        categoryColors[category] || categoryColors.general
      )}
    >
      {/* Icon */}
      {prompt.icon && (
        <div className="mb-2 text-2xl">{prompt.icon}</div>
      )}

      {/* Title */}
      <h4
        className={cn(
          'mb-1 font-semibold text-sm',
          categoryTextColors[category] || categoryTextColors.general
        )}
      >
        {prompt.title}
      </h4>

      {/* Prompt Preview */}
      <p className="text-xs text-text-secondary line-clamp-2">
        {prompt.prompt}
      </p>

      {/* Hover indicator */}
      <div
        className="absolute bottom-2 right-2 opacity-0
        transition-opacity group-hover:opacity-100"
      >
        <div
          className="rounded-full bg-stellar-primary p-1
          text-white"
        >
          <svg
            className="h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </button>
  );
}

/**
 * Empty State Component
 * Shown when no messages exist yet
 */
interface EmptyStateWithPromptsProps {
  onSelectPrompt: (prompt: string) => void;
  /** Opens the in-app user guide. When omitted, the "new here?" link is hidden. */
  onOpenGuide?: () => void;
}

export function EmptyStateWithPrompts({
  onSelectPrompt,
  onOpenGuide,
}: EmptyStateWithPromptsProps) {
  const { config } = useAdmin();
  const { selectedAgent } = useAgent();
  const { user } = useAuth();
  const { welcomeMessage, branding } = config;

  // First name for a personalized greeting. Optional chaining short-circuits
  // the whole chain if user/name is nullish; a whitespace-only name trims to
  // '' and is treated as absent by the truthiness guard below.
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? '';

  // Time-of-day greeting based on the user's local clock.
  const greeting = getTimeOfDayGreeting(new Date().getHours());

  // Determine title and subtitle with fallback hierarchy:
  // 1. Agent-specific welcome message (if enabled)
  // 2. Admin global welcome message (if enabled)
  // 3. Branding title fallback (personalized with the user's first name)
  let title: string;
  let subtitle: string;

  if (
    selectedAgent?.welcomeMessage?.useCustomMessage &&
    selectedAgent.welcomeMessage.title
  ) {
    // Use agent-specific welcome message
    title = selectedAgent.welcomeMessage.title;
    subtitle =
      selectedAgent.welcomeMessage.subtitle ||
      'Select a suggested prompt below or type your question';
  } else if (welcomeMessage.useCustomMessage && welcomeMessage.title) {
    // Use admin global welcome message
    title = welcomeMessage.title;
    subtitle =
      welcomeMessage.subtitle ||
      'Get started by selecting a suggested prompt or type your own question';
  } else {
    // Fallback to branding title — greet the user by name + time of day
    title = firstName
      ? `${greeting}, ${firstName}! Welcome to ${branding.headerTitle}!`
      : `Welcome to ${branding.headerTitle}`;
    subtitle =
      'Get started by selecting a suggested prompt or type your own question';
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      {/* Welcome Message */}
      <div className="mb-8 text-center max-w-2xl">
        <div
          className="mx-auto mb-4 flex h-20 w-20 items-center
          justify-center rounded-full bg-stellar-primary"
        >
          <Sparkles className="h-10 w-10 text-white" />
        </div>
        <div className="mb-2 text-2xl font-bold text-text-primary prose prose-sm max-w-none">
          <ReactMarkdown>{title}</ReactMarkdown>
        </div>
        <div className="text-text-secondary prose prose-sm max-w-none">
          <ReactMarkdown>{subtitle}</ReactMarkdown>
        </div>

        {onOpenGuide && (
          <button
            type="button"
            onClick={onOpenGuide}
            className={cn(
              'mt-4 inline-flex items-center gap-1.5 text-sm font-medium',
              'text-stellar-primary hover:underline',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-stellar-primary',
              'focus-visible:ring-offset-2 rounded'
            )}
          >
            <BookOpen className="h-4 w-4" />
            New here? Read the user guide
          </button>
        )}
      </div>

      {/* Suggested Prompts */}
      <div className="w-full max-w-4xl">
        <SuggestedPrompts onSelectPrompt={onSelectPrompt} />
      </div>
    </div>
  );
}
